import React, { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import styled, { DefaultTheme, withTheme } from 'styled-components';
import {
    requiredValueConfig,
    conditionalEmailValidationConfig,
    countrySpecificOrganizationNumberValidationConfig,
    organizationNumberFormatHint,
    allowedUrlsValidationConfig,
} from '../../../../types/ValidationTypes';
import { parseUrlLines } from '../../../../services/helpers/urlList';
import Spinner from '../../../atoms/Spinner/Spinner';
import ErrorMessage from '../../../atoms/Message/Error/ErrorMessage';
import ModalContainer from '../../../atoms/Modal/ModalContainer';
import HeaderTitle from '../../../atoms/Modal/HeaderTitle';
import FormRow from '../../../atoms/Form/FormRow';
import Flex from '../../../atoms/Box/Flex';
import Text from '../../../atoms/Text/Text';
import GridInput from '../../../atoms/Input/GridInput';
import GridTextArea from '../../../atoms/Input/GridTextArea';
import FooterButtonContainer from '../../../atoms/Modal/FooterButtonContainer';
import { ErrorType } from '../../../../types/response/ErrorCodes';
import SuccessMessage from '../../../atoms/Message/Success/SuccessMessage';
import Button from '../../../atoms/Button/Button';
import SelectInput from '../../../atoms/Input/SelectInput';
import Toggle from '../../../atoms/Toggle/Toggle';
import { merchantCountryOptions } from '../Merchants/merchantCountryOptions';
import { CreateOrganizationRequest } from '../../../../types/Organization';
import { useOrganizationsStore } from '../../../../store/admin/organizations/organizationsStore';
import { toAllowedCodes } from '../../../../types/onboarding/Merchant';
import AllowedMarketsAndSegments from '../Merchants/AllowedMarketsAndSegments';

interface CreateOrganizationFormProps {
    onCreate: (request: CreateOrganizationRequest) => void;
    hide: () => void;
    theme: DefaultTheme;
}

type Inputs = {
    name: string;
    merchantName: string;
    organizationNumber: string;
    /** One https:// URL per line; parsed into the allowed URLs of the merchant on submit. */
    allowedUrls: string;
    adminEmail: string;
};

export const allowedUrlsHint =
    'One https:// address per line, without a path, such as https://shop.example.se. The first one becomes the website of the merchant, and a store can only be onboarded on one of them.';

const CreateOrganizationForm: React.FC<CreateOrganizationFormProps> = ({
    onCreate,
    hide,
    theme,
}: CreateOrganizationFormProps) => {
    const {
        isCreatingOrganization,
        isCreatedOrganization,
        createOrganizationError,
        addMerchantError,
        inviteAdministratorError,
        createdOrganizationName,
    } = useOrganizationsStore();

    const [country, setCountry] = useState('SE');
    const [sendInvitation, setSendInvitation] = useState(true);
    const [allowedMarkets, setAllowedMarkets] = useState<string[]>([]);
    const [allowedSegments, setAllowedSegments] = useState<string[]>([]);

    const form = useForm<Inputs>({
        mode: 'onSubmit',
        shouldUseNativeValidation: true,
        defaultValues: {
            name: '',
            merchantName: '',
            organizationNumber: '',
            allowedUrls: '',
            adminEmail: '',
        },
    });
    const {
        getValues,
        register,
        handleSubmit,
        trigger,
        formState: { isSubmitted },
    } = form;

    const name = register('name', requiredValueConfig);
    const merchantName = register('merchantName', requiredValueConfig);
    const organizationNumber = register(
        'organizationNumber',
        countrySpecificOrganizationNumberValidationConfig(country)
    );
    const allowedUrls = register('allowedUrls', allowedUrlsValidationConfig);
    const adminEmail = register('adminEmail', conditionalEmailValidationConfig(sendInvitation));

    // The email rules depend on the toggle, but a validation error already shown (natively, on the input) is only
    // re-evaluated when the field itself changes. Re-validate after a submit so toggling the invitation off clears
    // a "Required field" error, and toggling it back on brings it back.
    useEffect(() => {
        if (isSubmitted) trigger('adminEmail');
    }, [sendInvitation, isSubmitted, trigger]);

    const isDisabled = isCreatingOrganization || isCreatedOrganization;

    const onSubmit = (data: Inputs) => {
        onCreate({
            name: data.name,
            merchantName: data.merchantName,
            countryCode: country,
            organizationNumber: data.organizationNumber,
            allowedUrls: parseUrlLines(data.allowedUrls),
            allowedMarkets: toAllowedCodes(allowedMarkets),
            allowedSegments: toAllowedCodes(allowedSegments),
            adminEmail: data.adminEmail,
            sendInvitation: sendInvitation,
        });
    };

    return (
        <>
            <ModalContainer position="header">
                <HeaderTitle>Create organization</HeaderTitle>
            </ModalContainer>

            <ModalContainer position="content" noScroll={false}>
                <Form
                    as="form"
                    onSubmit={handleSubmit(onSubmit)}
                    data-testid="form"
                    id="create-organization-form"
                >
                    <FormRow first last>
                        <GridInput
                            type="text"
                            id="name"
                            placeholder="Organization name"
                            placeholderRaised={!!getValues(name.name)}
                            setRef={name.ref}
                            name={name.name}
                            onChange={name.onChange}
                            disabled={isDisabled}
                            dataTestId="organization-name"
                        />
                    </FormRow>
                    <Section>
                        <Text medium>Merchant</Text>
                        <Text as="p" secondary small>
                            The legal entity registered under the organization number.
                        </Text>
                        <Rows>
                            <FormRow first>
                                <GridInput
                                    type="text"
                                    id="merchantName"
                                    placeholder="Merchant legal name"
                                    placeholderRaised={!!getValues(merchantName.name)}
                                    setRef={merchantName.ref}
                                    name={merchantName.name}
                                    onChange={merchantName.onChange}
                                    disabled={isDisabled}
                                    dataTestId="merchant-name"
                                />
                            </FormRow>
                            <FormRow last>
                                <SelectInput
                                    id="country"
                                    dataTestId="create-organization-country-selector"
                                    placeholder="Country"
                                    onChange={setCountry}
                                    value={country}
                                    options={merchantCountryOptions}
                                    disabled={isDisabled}
                                    setHeight={false}
                                />
                                <GridInput
                                    type="text"
                                    id="organizationNumber"
                                    placeholder="Organization number"
                                    placeholderRaised={!!getValues(organizationNumber.name)}
                                    setRef={organizationNumber.ref}
                                    name={organizationNumber.name}
                                    onChange={organizationNumber.onChange}
                                    disabled={isDisabled}
                                    dataTestId="organization-number"
                                />
                            </FormRow>
                        </Rows>
                        <Text as="p" secondary small data-testid="organization-number-hint">
                            {organizationNumberFormatHint(country)}
                        </Text>
                    </Section>
                    <Section>
                        <FormRow first last>
                            <GridTextArea
                                id="allowedUrls"
                                placeholder="Allowed store URLs"
                                placeholderRaised={!!getValues(allowedUrls.name)}
                                setRef={allowedUrls.ref}
                                name={allowedUrls.name}
                                onChange={allowedUrls.onChange}
                                disabled={isDisabled}
                                dataTestId="allowed-urls"
                            />
                        </FormRow>
                        <Text as="p" secondary small>
                            {allowedUrlsHint}
                        </Text>
                    </Section>
                    <AllowedMarketsAndSegments
                        markets={allowedMarkets}
                        segments={allowedSegments}
                        onMarketsChange={setAllowedMarkets}
                        onSegmentsChange={setAllowedSegments}
                        disabled={isDisabled}
                    />
                    <Section>
                        <Text medium>Invite an administrator</Text>
                        <Text as="p" secondary small>
                            This person gets full access to the new organization.
                        </Text>
                        <FormRow first last>
                            <GridInput
                                type="text"
                                id="adminEmail"
                                placeholder={
                                    sendInvitation ? 'Email address' : 'Email address (optional)'
                                }
                                placeholderRaised={!!getValues(adminEmail.name)}
                                setRef={adminEmail.ref}
                                name={adminEmail.name}
                                onChange={adminEmail.onChange}
                                disabled={isDisabled}
                                dataTestId="admin-email"
                            />
                        </FormRow>
                        <ToggleRow>
                            <Toggle
                                id="sendInvitation"
                                data-testid="send-invitation-toggle"
                                checked={sendInvitation}
                                onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                                    setSendInvitation(e.target.checked)
                                }
                                disabled={isDisabled}
                            />
                            <Text as="label" htmlFor="sendInvitation" clickable>
                                Send the invitation now
                            </Text>
                        </ToggleRow>
                    </Section>

                    {isCreatedOrganization && (
                        <Result>
                            <SuccessMessage>
                                Organization {createdOrganizationName} was created!
                            </SuccessMessage>
                        </Result>
                    )}
                </Form>
            </ModalContainer>

            {(createOrganizationError || addMerchantError || inviteAdministratorError) && (
                <ModalContainer position="error">
                    {createOrganizationError && (
                        <ErrorMessage
                            error={createOrganizationError}
                            errorHeader={ErrorType.CreateOrganization}
                        />
                    )}
                    {addMerchantError && (
                        <ErrorMessage
                            error={addMerchantError}
                            errorHeader={ErrorType.AddMerchantToOrganization}
                        />
                    )}
                    {inviteAdministratorError && (
                        <ErrorMessage
                            error={inviteAdministratorError}
                            errorHeader={ErrorType.InviteToCreatedOrganization}
                        />
                    )}
                </ModalContainer>
            )}

            <ModalContainer position="footer">
                <FooterButtonContainer>
                    {!isCreatedOrganization && (
                        <Button onClick={hide} large>
                            Cancel
                        </Button>
                    )}
                    {!isCreatedOrganization && (
                        <Button
                            tabIndex={0}
                            form="create-organization-form"
                            type="submit"
                            large
                            primary
                            disabled={isDisabled}
                            data-testid="create-organization-submit-button"
                        >
                            {isCreatingOrganization ? (
                                <Spinner color={theme.colors.light} size={8} loading />
                            ) : (
                                <span>Create organization</span>
                            )}
                        </Button>
                    )}
                    {isCreatedOrganization && (
                        <Button onClick={hide} large primary>
                            Close
                        </Button>
                    )}
                </FooterButtonContainer>
            </ModalContainer>
        </>
    );
};

/** The sections of the form, stacked with room between them. */
const Form = styled(Flex).attrs(props => ({
    column: true,
    gap: props.theme.layout.gap.medium,
}))``;

/** One section: its label, rows and hint, stacked close together. */
const Section = styled(Flex).attrs(props => ({
    column: true,
    gap: props.theme.layout.gap.xsmall,
}))``;

/** Rows that share their borders, stacked with no room between them. */
const Rows = styled(Flex).attrs({ column: true })``;

/** The toggle and its label on one line. */
const ToggleRow = styled(Flex).attrs(props => ({
    alignItems: 'center',
    gap: props.theme.layout.gap.small,
}))``;

/** The result message, centered. */
const Result = styled(Flex).attrs({ justifyContent: 'center' })``;

export default withTheme(CreateOrganizationForm) as React.ComponentType<
    Omit<CreateOrganizationFormProps, 'theme'>
>;
